import axios from 'axios';

const API_KEY = process.env.REACT_APP_API_KEY;

// Use OAuth 2.0 Client ID for authentication (different from API Key)
// If CLIENT_ID is not set, fall back to API_KEY for backward compatibility
const clientId = process.env.REACT_APP_CLIENT_ID || API_KEY;

// Use our Cloudflare Worker proxy instead of direct API calls
export const fetchTrendingPosts = async (topic, forceRefresh = false) => {
  try {
    // Call our Cloudflare Worker function
    const response = await axios.get('/api/tweets', {
      params: { 
        topic,
        refresh: forceRefresh ? 'true' : 'false'
      }
    });

    return {
      posts: response.data.posts || [],
      logs: response.data.logs || [],
      source: response.data.source || 'unknown'
    };
  } catch (error) {
    console.error('Error fetching posts:', error);
    return {
      posts: [],
      logs: ['❌ Error: ' + error.message],
      source: 'error'
    };
  }
};

export const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64url(new Uint8Array(hash));
};

const base64url = (array) => {
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const exchangeCodeForToken = async (code, codeVerifier) => {
  const params = new URLSearchParams();
  params.append('grant_type', 'authorization_code');
  params.append('code', code);
  
  // Use the SAME redirect URI that was used during authorization
  let redirectUri = localStorage.getItem('redirect_uri');
  
  // Fallback if not in localStorage
  if (!redirectUri) {
    if (window.location.hostname.includes('localhost') || window.location.hostname === '127.0.0.1') {
      redirectUri = 'http://localhost:3000';
    } else {
      redirectUri = 'https://x-newspaper.com';
    }
  }
    
  params.append('redirect_uri', redirectUri);
  params.append('client_id', clientId);
  params.append('code_verifier', codeVerifier);

  // Use our Cloudflare Worker proxy to avoid CORS
  const response = await axios.post('/api/token', params.toString(), {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  });
  return response.data.access_token;
};

export const getUser = async (token) => {
  // Use our Cloudflare Worker proxy to avoid CORS
  const response = await axios.get('/api/user', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data.data;
};

export const postReply = async (tweetId, text, token) => {
  const response = await axios.post('/tweets', { text, reply: { in_reply_to_tweet_id: tweetId } }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

export const likeTweet = async (tweetId, userId, token) => {
  // Get personalization_id from localStorage (required to avoid error 357)
  const personalizationId = localStorage.getItem('personalization_id');
  
  // Use our Cloudflare Worker proxy to avoid CORS
  // Using X API v2 (same as working replies)
  const response = await axios.post('/api/like', 
    { userId, tweetId, personalizationId }, 
    {
      headers: { 'Authorization': `Bearer ${token}` }
    }
  );
  return response.data;
};

export const fetchReplies = async (tweetId) => {
  try {
    const response = await axios.get('/api/replies', {
      params: { tweetId }
    });
    return response.data.replies || [];
  } catch (error) {
    console.error('Error fetching replies:', error);
    return [];
  }
};

export const fetchRepliesForPost = async (tweetId) => {
  try {
    const response = await axios.get('/api/showRepliesForPost', {
      params: { tweetId }
    });
    return response.data.replies || [];
  } catch (error) {
    console.error('Error fetching replies for post:', error);
    return [];
  }
};

export const postReplyToTweet = async (tweetId, text, accessToken, userId, mediaFile = null) => {
  try {
    // Get personalization_id from localStorage (required for write operations)
    const personalizationId = localStorage.getItem('personalization_id');
    
    // Use FormData to support file uploads
    const formData = new FormData();
    formData.append('tweetId', tweetId);
    formData.append('text', text);
    formData.append('accessToken', accessToken);
    formData.append('userId', userId);
    
    if (personalizationId) {
      formData.append('personalizationId', personalizationId);
    }
    
    if (mediaFile) {
      formData.append('media', mediaFile);
    }
    
    const response = await axios.post('/api/postReply', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error posting reply:', error);
    throw error;
  }
};

export const fetchUserLists = async (userId, token) => {
  try {
    const response = await axios.get('/api/userLists', {
      params: { userId },
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data.lists || [];
  } catch (error) {
    console.error('Error fetching user lists:', error);
    return [];
  }
};

export const fetchPopularLists = async () => {
  try {
    const response = await axios.get('/api/popularLists');
    return response.data.lists || [];
  } catch (error) {
    console.error('Error fetching popular lists:', error);
    return [];
  }
};

export const fetchListPosts = async (listId, isLoggedIn = false) => {
  try {
    const response = await axios.get('/api/listPosts', {
      params: { listId, isLoggedIn: isLoggedIn ? 'true' : 'false' }
    });
    return {
      posts: response.data.posts || [],
      logs: response.data.logs || [],
      source: response.data.source || 'unknown'
    };
  } catch (error) {
    console.error('Error fetching list posts:', error);
    return {
      posts: [],
      logs: ['❌ Error: ' + error.message],
      source: 'error'
    };
  }
};

export { clientId };
